import React, { useRef, useState, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperType } from 'swiper';
import { Navigation, Pagination, A11y } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import * as Styled from './OCardCarouselCombo.styles';
import { OCardCarouselComboMContentHeaderMButton, OCardCarouselComboProps } from './OCardCarouselCombo.types';
import { MContentHeader, resolveRawButton } from '../../molecules/MContentHeader/index';
import { MCarouselControls } from '../../molecules/MCarouselControls/index';
import { MSlot } from '../../molecules/MSlot/index';
import {
  MContentHeaderAlignment,
  MContentHeaderAllowedButtonType,
  type MContentHeaderButton,
} from '../../molecules/MContentHeader/MContentHeader.types';
import { MCarouselControlsType } from '../../molecules/MCarouselControls/MCarouselControls.types';
import { ViewportPx } from '../../utils/ImageUtils';

const COMPONENT_NAME = 'o-mi-web-ui-kit-OCardCarouselCombo';
const TEST_ID = 'mi-web-ui-kit-OCardCarouselCombo';
const AUTHORING_CLASS = 'glide__slides_authoring';

const isAuthoredItem = (item: unknown): boolean => {
  if (!item || typeof item !== 'object') {
    return false;
  }

  const itemRecord = item as Record<string, unknown>;
  const nestedItems = (itemRecord[':items'] ?? itemRecord['cqItems']) as Record<string, unknown> | undefined;
  const nestedItemsOrder = (itemRecord[':itemsOrder'] ?? itemRecord['cqItemsOrder']) as string[] | undefined;

  return (
    Boolean(itemRecord[':type']) ||
    Boolean(nestedItems && Object.keys(nestedItems).length > 0) ||
    Boolean(nestedItemsOrder?.length)
  );
};

const getAuthoredItemKeys = (itemsOrder: string[], items: Record<string, unknown>): string[] => {
  const orderedKeys = itemsOrder.filter((key: string) => Boolean(items[key]));
  const orderedKeySet = new Set(orderedKeys);
  const fallbackKeys = Object.keys(items).filter(key => !orderedKeySet.has(key) && isAuthoredItem(items[key]));

  return [...orderedKeys, ...fallbackKeys];
};

const getHighestAuthoredItemIndex = (authoredItemKeys: string[]): number =>
  authoredItemKeys.reduce((maxIndex: number, key: string) => {
    const itemKeyMatch = /^item(\d+)$/.exec(key);
    if (!itemKeyMatch) {
      return maxIndex;
    }

    return Math.max(maxIndex, Number(itemKeyMatch[1]));
  }, -1);

const getConfiguredNoOfCards = (...values: unknown[]): number => {
  const configuredValue = values.find(value => value !== undefined && value !== null && String(value).trim() !== '');
  const parsedNoOfCards = Number(configuredValue ?? 0);

  return Number.isFinite(parsedNoOfCards) && parsedNoOfCards > 0 ? parsedNoOfCards : 0;
};

const buildAuthorSlotKeys = (
  authoredItemKeys: string[],
  items: Record<string, unknown>,
  desiredSlotCount: number
): string[] => {
  const slotKeys = [...authoredItemKeys];
  if (desiredSlotCount <= slotKeys.length) {
    return slotKeys;
  }

  const usedKeys = new Set<string>([...Object.keys(items), ...slotKeys]);
  let nextCandidateIndex = getHighestAuthoredItemIndex(slotKeys) + 1;

  while (slotKeys.length < desiredSlotCount) {
    while (usedKeys.has(`item${nextCandidateIndex}`)) {
      nextCandidateIndex += 1;
    }

    const generatedKey = `item${nextCandidateIndex}`;
    slotKeys.push(generatedKey);
    usedKeys.add(generatedKey);
    nextCandidateIndex += 1;
  }

  return slotKeys;
};

function toContentHeaderButton(
  btn: OCardCarouselComboMContentHeaderMButton | undefined
): MContentHeaderButton | undefined {
  if (!btn) return undefined;
  return {
    buttonText: btn.buttonText,
    buttonType: btn.buttonType as MContentHeaderAllowedButtonType,
    size: btn.size,
    ctaLink: btn.ctaLink,
    openInANewTab: btn.openInANewTab === 'true',
    externalLink: btn.externalLink === 'true',
    icon: btn.icon,
    aIcon: btn.aIcon,
  };
}

const normalizeHeaderAlignment = (alignment?: string): MContentHeaderAlignment => {
  const normalized = alignment?.trim().toLowerCase();
  if (normalized === 'center' || normalized === 'centered') {
    return MContentHeaderAlignment.Centered;
  }
  return MContentHeaderAlignment.Left;
};

export const OCardCarouselCombo = (props: OCardCarouselComboProps) => {
  const swiperRef = useRef<{ swiper: SwiperType } | null>(null);
  const [totalSlides, setTotalSlides] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAuthoringClassEnabled, setIsAuthoringClassEnabled] = useState(false);

  const {
    peeking: peekingProp,
    noOfCards: noOfCardsProp,
    mContentHeader: mContentHeaderProp,
    mCarouselControls: mCarouselControlsProp,
    pagePath: pagePathProp,
    itemPath: itemPathProp,
    isAuthorMode = false,
    ':items': itemsProp,
    cqItems: cqItemsProp,
    aemReactComponentMapping = {},
    ':itemsOrder': itemsOrderProp,
    className = '',
  } = props;

  // In AEM, EditableComponent passes the model JSON nested under `props.model`,
  // whereas Storybook spreads the same fields at the top level. Resolve each
  // field from the top-level prop first, then fall back to `props.model`.
  const model = (props.model || {}) as Record<string, unknown>;
  const peeking = (peekingProp ?? (model['peeking'] as boolean | undefined) ?? false) as boolean;
  const mContentHeader = mContentHeaderProp ?? (model['mContentHeader'] as typeof mContentHeaderProp);
  const mCarouselControls = mCarouselControlsProp ?? (model['mCarouselControls'] as typeof mCarouselControlsProp);
  const pagePath = pagePathProp || (model['pagePath'] as string | undefined) || '';
  const itemPath = itemPathProp || (model['itemPath'] as string | undefined) || '';
  const items = (itemsProp ??
    (model[':items'] as typeof itemsProp) ??
    cqItemsProp ??
    (model['cqItems'] as typeof cqItemsProp) ??
    {}) as NonNullable<typeof itemsProp>;
  const itemsOrder = (itemsOrderProp ??
    (model[':itemsOrder'] as string[] | undefined) ??
    ((props as Record<string, unknown>)['cqItemsOrder'] as string[] | undefined) ??
    (model['cqItemsOrder'] as string[] | undefined) ??
    []) as string[];

  const handleSlideChange = (swiper: SwiperType) => {
    setCurrentSlide(swiper.snapIndex);
  };

  const updatePaginationState = (swiper: SwiperType) => {
    const nextTotalSlides = Math.max(swiper.snapGrid.length, 1);
    setTotalSlides(prev => (prev === nextTotalSlides ? prev : nextTotalSlides));
    setCurrentSlide(prev => (prev === swiper.snapIndex ? prev : swiper.snapIndex));
  };

  const handlePrev = () => {
    swiperRef.current?.swiper.slidePrev();
  };

  const handleNext = () => {
    swiperRef.current?.swiper.slideNext();
  };

  const getSlidesPerView = () => {
    if (peeking) {
      return 'auto';
    }
    return 1;
  };

  const getSpaceBetween = () => {
    return 16;
  };

  const itemsRecord = items as Record<string, unknown>;
  const authoredItemKeys = getAuthoredItemKeys(itemsOrder, itemsRecord);
  const effectiveNoOfCards = getConfiguredNoOfCards(model['noOfCards'], noOfCardsProp);
  const authorSlotCount = Math.max(effectiveNoOfCards, authoredItemKeys.length);
  const slideItemKeys = isAuthorMode
    ? buildAuthorSlotKeys(authoredItemKeys, itemsRecord, authorSlotCount)
    : authoredItemKeys;
  const swiperRenderKey = `${itemPath}-${effectiveNoOfCards}-${slideItemKeys.join('|')}`;
  const headerAlignment = normalizeHeaderAlignment(mContentHeader?.alignment);
  const normalizedControlType = String(mCarouselControls?.type || '').toLowerCase();
  const controlType =
    normalizedControlType === MCarouselControlsType.Numbers
      ? MCarouselControlsType.Numbers
      : MCarouselControlsType.Dots;

  useEffect(() => {
    const pageWcmMode = document.getElementsByTagName('html')[0];
    if (!pageWcmMode) {
      setIsAuthoringClassEnabled(false);
      return;
    }

    const syncAuthoringClassState = () => {
      if (!isAuthorMode) {
        setIsAuthoringClassEnabled(false);
        return;
      }

      const currWcmMode = pageWcmMode.getAttribute('class');
      if (currWcmMode?.includes('Edit')) {
        setIsAuthoringClassEnabled(true);
      } else if (currWcmMode?.includes('Preview')) {
        setIsAuthoringClassEnabled(false);
      }
    };

    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.attributeName === 'class') {
          syncAuthoringClassState();
        }
      });
    });

    observer.observe(pageWcmMode, { attributes: true });
    syncAuthoringClassState();

    return () => {
      observer.disconnect();
    };
  }, [isAuthorMode, swiperRenderKey]);

  useEffect(() => {
    if (swiperRef.current?.swiper) {
      updatePaginationState(swiperRef.current.swiper);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, peeking]);

  return (
    <Styled.Wrapper className={className} data-component-name={COMPONENT_NAME} data-testid={TEST_ID} $peeking={peeking}>
      <Styled.CarouselContainer>
        {mContentHeader && (
          <Styled.HeaderWrapper $alignment={headerAlignment}>
            <MContentHeader
              alignment={headerAlignment}
              mTextBlockSimple={mContentHeader.mTextBlockSimple}
              cta={mContentHeader.cta as 'true' | 'false' | undefined}
              secondaryCta={
                // `secondaryCta` may be authored at the top level or controlled via
                // `mContentSwap.button2` for AEM models that use the swap sub-model.
                // `button2: 'false'` (string or boolean false) suppresses the secondary CTA.
                (mContentHeader.secondaryCta ??
                  (mContentHeader.mContentSwap?.button2 != null
                    ? String(mContentHeader.mContentSwap.button2)
                    : undefined)) as 'true' | 'false' | undefined
              }
              mButton={toContentHeaderButton(
                resolveRawButton(mContentHeader.mButton, mContentHeader.mContentSwap?.mButton)
              )}
              mButton2={toContentHeaderButton(
                resolveRawButton(mContentHeader.mButton2, mContentHeader.mContentSwap?.mButton2)
              )}
            />
          </Styled.HeaderWrapper>
        )}

        <Styled.CarouselSection>
          <Styled.SwiperWrapper $peeking={peeking}>
            <Swiper
              key={swiperRenderKey}
              ref={swiperRef}
              className={isAuthoringClassEnabled ? AUTHORING_CLASS : undefined}
              modules={[Navigation, Pagination, A11y]}
              spaceBetween={getSpaceBetween()}
              slidesPerView={getSlidesPerView()}
              onSwiper={(swiper: SwiperType) => {
                updatePaginationState(swiper);
              }}
              onSlideChange={(swiper: SwiperType) => {
                handleSlideChange(swiper);
                updatePaginationState(swiper);
              }}
              onBreakpoint={(swiper: SwiperType) => {
                updatePaginationState(swiper);
              }}
              breakpoints={{
                [ViewportPx.MD]: {
                  slidesPerView: 'auto',
                  spaceBetween: 24,
                },
                [ViewportPx.LG]: {
                  slidesPerView: peeking ? 'auto' : 3,
                  spaceBetween: 24,
                },
                [ViewportPx.XL]: {
                  slidesPerView: peeking ? 'auto' : 2,
                  spaceBetween: 24,
                },
              }}
            >
              {slideItemKeys.map((key: string) => {
                const itemData = itemsRecord[key] as Record<string, unknown> | undefined;

                type AemChild = {
                  ':type'?: string;
                  ':items'?: Record<string, AemChild>;
                  ':itemsOrder'?: string[];
                };

                const childItem = (itemData ?? {}) as AemChild;
                const childResourceType = (childItem[':type'] ?? itemData?.[':type']) as string | undefined;
                const childItems = (childItem[':items'] ?? itemData?.['cqItems'] ?? {}) as Record<string, AemChild>;
                const childItemsOrder = (childItem[':itemsOrder'] ?? itemData?.['cqItemsOrder'] ?? []) as string[];
                const hasNestedItems = Object.keys(childItems).length > 0;
                const DirectChildComponent = childResourceType
                  ? aemReactComponentMapping[childResourceType]
                  : undefined;
                const shouldRenderDirectChild = Boolean(itemData) && !hasNestedItems && Boolean(DirectChildComponent);

                return (
                  <SwiperSlide key={key}>
                    <Styled.SlideContent $peeking={peeking}>
                      {shouldRenderDirectChild && DirectChildComponent ? (
                        <DirectChildComponent
                          model={itemData as Record<string, unknown>}
                          {...(itemData as Record<string, unknown>)}
                        />
                      ) : (
                        <MSlot
                          pagePath={pagePath}
                          itemPath={`${itemPath}/${key}`}
                          isAuthorMode={isAuthorMode}
                          cqItems={childItems}
                          model={{
                            ...(model as Record<string, unknown>),
                            ':order': childItemsOrder,
                            cqItems: items,
                            cqItemsOrder: itemsOrder,
                            cqType: (model as Record<string, unknown>)[':type'],
                            itemPath,
                            pagePath,
                          }}
                          aemReactComponentMapping={aemReactComponentMapping}
                        />
                      )}
                    </Styled.SlideContent>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </Styled.SwiperWrapper>

          {mCarouselControls && totalSlides > 1 && (
            <Styled.ControlsWrapper $imageOverlay={mCarouselControls.imageOverlay === 'true'}>
              <MCarouselControls
                type={controlType}
                imageOverlay={mCarouselControls.imageOverlay === 'true'}
                totalSlides={totalSlides}
                currentSlide={currentSlide}
                onPrev={handlePrev}
                onNext={handleNext}
              />
            </Styled.ControlsWrapper>
          )}
        </Styled.CarouselSection>
      </Styled.CarouselContainer>
    </Styled.Wrapper>
  );
};
